import { type WritableDraft } from 'immer';
import { type Observable, of } from 'rxjs';

import { type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';

import { type EngineState } from '../../../+state/engine';
import {
  AbstractAiElementHandler,
  applyFieldSize,
  getElement,
  getInitialProps,
  patchFieldLabel,
  upsertElement,
} from '../../../ai';

import { type QrCodeContext, type QrCodeDefinition } from './qr-code.definition';

export class QrCodeElementHandler extends AbstractAiElementHandler<'qrcode'> {
  readonly type = 'qrcode';

  prepare(context: QrCodeContext): Observable<QrCodeContext> {
    this.resolveElementIcon(context.element);

    return of(context);
  }

  process(context: QrCodeContext, draft: WritableDraft<EngineState>): void {
    let props: PartialPropsOf<'qrcode'> = getInitialProps<'qrcode'>(draft, context.element.id, {});
    props = patchFieldLabel<'qrcode'>(props, context.element);
    props = this.applyUrlTarget(props, context.element);

    const { elementId } = upsertElement(draft, context, props);

    applyFieldSize<'qrcode'>(getElement(draft, elementId, 'qrcode'), context.element, {
      keepRegisteredWidthWhenOmitted: true,
      fixedHeight: true,
    });
  }

  private applyUrlTarget(
    props: PartialPropsOf<'qrcode'>,
    element: QrCodeDefinition,
  ): PartialPropsOf<'qrcode'> {
    if (element.field) {
      if (element.url) {
        console.warn(`QR Code (id:${element.id}): both "field" and "url" set; binding to "field"`);
      }
      return applyPropsPatch<'qrcode'>(props, { content: { url: { type: 'dynamic' } } });
    }

    if (element.url?.$type === 'url') {
      return applyPropsPatch<'qrcode'>(props, {
        content: { url: { type: 'add', value: element.url.value } },
      });
    }

    if (element.url?.$type === 'currentPage') {
      return applyPropsPatch<'qrcode'>(props, { content: { url: { type: 'current' } } });
    }

    return props;
  }
}
